import React, { useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Tooltip,
  Grid,
  MenuItem,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";

const CampaignAiTable = () => {
  const [tableData, setTableData] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    customerId: "",
    databaseCategory: "",
    templateId: "",
    queryDescription: "",
    campaignDate: "",
  });

  // Sample options for dropdowns
  const databaseCategories = [
    "Retail",
    "Healthcare",
    "Finance",
    "Technology",
    "Education",
  ];

  const templateIds = [
    "TEMP_001",
    "TEMP_002",
    "TEMP_003",
    "TEMP_004",
    "TEMP_005",
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddItem = () => {
    if (Object.values(formData).some((value) => value === "")) {
      alert("Please fill all fields");
      return;
    }

    const newItem = {
      id: Date.now(), // Simple unique ID
      ...formData,
    };

    setTableData((prev) => [...prev, newItem]);
    setFormData({
      customerId: "",
      databaseCategory: "",
      templateId: "",
      queryDescription: "",
      campaignDate: "",
    });
    setOpenDialog(false);
  };

  const handleDeleteItem = (id) => {
    setTableData((prev) => prev.filter((item) => item.id !== id));
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({
      customerId: "",
      databaseCategory: "",
      templateId: "",
      queryDescription: "",
      campaignDate: "",
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5" component="h1" fontWeight="bold">
          Campaign Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
          sx={{
            backgroundColor: "#1976d2",
            "&:hover": {
              backgroundColor: "#1565c0",
            },
          }}
        >
          Add Campaign
        </Button>
      </Box>

      <TableContainer component={Paper} elevation={3}>
        <Table sx={{ minWidth: 650 }} aria-label="campaign table">
          <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Customer ID</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                Database Category
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Template ID</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                Query Description
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Campaign Date</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tableData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="textSecondary">
                    No campaigns added yet. Click "Add Campaign" to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              tableData.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    "&:hover": { backgroundColor: "#fafafa" },
                  }}
                >
                  <TableCell>{row.customerId}</TableCell>
                  <TableCell>{row.databaseCategory}</TableCell>
                  <TableCell>{row.templateId}</TableCell>
                  <TableCell>{row.queryDescription}</TableCell>
                  <TableCell>{row.campaignDate}</TableCell>
                  <TableCell>
                    <Tooltip title="Delete">
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteItem(row.id)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Campaign Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">
            Add New Campaign
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Customer ID"
                name="customerId"
                value={formData.customerId}
                onChange={handleInputChange}
                required
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Database Category"
                name="databaseCategory"
                value={formData.databaseCategory}
                onChange={handleInputChange}
                required
                variant="outlined"
              >
                {databaseCategories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Template ID"
                name="templateId"
                value={formData.templateId}
                onChange={handleInputChange}
                required
                variant="outlined"
              >
                {templateIds.map((template) => (
                  <MenuItem key={template} value={template}>
                    {template}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Campaign Date"
                name="campaignDate"
                type="date"
                value={formData.campaignDate}
                onChange={handleInputChange}
                required
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Query Description"
                name="queryDescription"
                value={formData.queryDescription}
                onChange={handleInputChange}
                required
                multiline
                rows={3}
                variant="outlined"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseDialog} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleAddItem}
            variant="contained"
            disabled={Object.values(formData).some((value) => value === "")}
          >
            Add Campaign
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CampaignAiTable;
